/**
 * ProgressBar - Displays curriculum progress with lesson navigation.
 */

import { memo, useState, useCallback } from 'react'
import { ChevronDown, Check, Lock, BookOpen } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { Lesson } from '../../curriculum/manager'

export interface ProgressBarProps {
  /** All lessons in the curriculum */
  lessons: Lesson[]
  /** Set of completed lesson IDs */
  completedLessons: Set<string>
  /** Currently active lesson ID */
  currentLessonId: string | null
  /** Callback when a lesson is selected */
  onSelectLesson?: (lessonId: string) => void
  /** Optional className */
  className?: string
}

/**
 * ProgressBar shows curriculum completion and lesson list.
 */
export const ProgressBar = memo(function ProgressBar({
  lessons,
  completedLessons,
  currentLessonId,
  onSelectLesson,
  className,
}: ProgressBarProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const completedCount = lessons.filter((l) => completedLessons.has(l.id)).length
  const totalCount = lessons.length
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0

  // Find current lesson details
  const currentLesson = lessons.find((l) => l.id === currentLessonId)
  const currentLessonNumber = currentLesson ? lessons.indexOf(currentLesson) + 1 : 0

  // Toggle lesson list
  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  // Handle lesson selection
  const handleLessonClick = useCallback(
    (lesson: Lesson) => {
      // Check if lesson is unlocked
      const isUnlocked =
        lesson.prerequisites.length === 0 ||
        lesson.prerequisites.every((prereq) => completedLessons.has(prereq))

      if (isUnlocked && onSelectLesson) {
        onSelectLesson(lesson.id)
        setIsExpanded(false)
      }
    },
    [completedLessons, onSelectLesson]
  )

  return (
    <div className={cn('relative', className)}>
      {/* Main progress bar header */}
      <button
        type="button"
        onClick={toggleExpanded}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3',
          'transition-colors hover:bg-muted/50',
          isExpanded && 'rounded-b-none border-b-0'
        )}
        aria-expanded={isExpanded}
        aria-controls="lesson-list"
      >
        {/* Progress ring */}
        <div className="relative h-10 w-10 flex-shrink-0">
          <svg className="h-10 w-10 -rotate-90" viewBox="0 0 36 36">
            {/* Background circle */}
            <circle
              cx="18"
              cy="18"
              r="15.9"
              fill="none"
              className="stroke-muted"
              strokeWidth="3"
            />
            {/* Progress circle */}
            <circle
              cx="18"
              cy="18"
              r="15.9"
              fill="none"
              className="stroke-primary transition-all duration-500"
              strokeWidth="3"
              strokeDasharray="100"
              strokeDashoffset={100 - progressPercentage}
              strokeLinecap="round"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground">
            {Math.round(progressPercentage)}%
          </span>
        </div>

        {/* Current lesson info */}
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-foreground">
            {currentLesson ? (
              <>Lesson {currentLessonNumber}: {currentLesson.title}</>
            ) : (
              'Select a lesson'
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {completedCount} of {totalCount} lessons completed
          </p>
        </div>

        {/* Expand indicator */}
        <ChevronDown
          className={cn(
            'h-5 w-5 text-muted-foreground transition-transform',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Lesson list dropdown */}
      {isExpanded && (
        <div
          id="lesson-list"
          className="absolute left-0 right-0 z-10 rounded-b-lg border border-t-0 border-border bg-card shadow-lg"
          role="listbox"
          aria-label="Lesson list"
        >
          <div className="max-h-64 overflow-y-auto">
            {lessons.map((lesson, idx) => {
              const isCompleted = completedLessons.has(lesson.id)
              const isCurrent = lesson.id === currentLessonId
              const isUnlocked =
                lesson.prerequisites.length === 0 ||
                lesson.prerequisites.every((prereq) => completedLessons.has(prereq))

              return (
                <button
                  key={lesson.id}
                  type="button"
                  onClick={() => handleLessonClick(lesson)}
                  disabled={!isUnlocked}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
                    isCurrent && 'bg-primary/10',
                    !isCurrent && isUnlocked && 'hover:bg-muted/50',
                    !isUnlocked && 'cursor-not-allowed opacity-50',
                    idx < lessons.length - 1 && 'border-b border-border'
                  )}
                  role="option"
                  aria-selected={isCurrent}
                  aria-disabled={!isUnlocked}
                >
                  {/* Status icon */}
                  <span
                    className={cn(
                      'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full',
                      isCompleted && 'bg-green-500 text-white',
                      isCurrent && !isCompleted && 'bg-primary text-primary-foreground',
                      !isCompleted && !isCurrent && isUnlocked && 'bg-muted text-muted-foreground',
                      !isUnlocked && 'bg-muted text-muted-foreground'
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4" />
                    ) : !isUnlocked ? (
                      <Lock className="h-3 w-3" />
                    ) : isCurrent ? (
                      <BookOpen className="h-3 w-3" />
                    ) : (
                      <span className="text-xs font-medium">{idx + 1}</span>
                    )}
                  </span>

                  {/* Lesson info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        'truncate text-sm font-medium',
                        isCurrent ? 'text-primary' : 'text-foreground'
                      )}
                    >
                      {lesson.title}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {lesson.description}
                    </p>
                  </div>

                  {/* Completion badge */}
                  {isCompleted && (
                    <span className="text-xs font-medium text-green-600">Done</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
})

/**
 * Simple linear progress bar without lesson list.
 */
export interface SimpleProgressBarProps {
  /** Progress percentage (0-100) */
  progress: number
  /** Label to display */
  label?: string
  /** Optional className */
  className?: string
}

export const SimpleProgressBar = memo(function SimpleProgressBar({
  progress,
  label,
  className,
}: SimpleProgressBarProps) {
  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{label}</span>
          <span className="font-medium text-foreground">{Math.round(progress)}%</span>
        </div>
      )}
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  )
})
